import { getAcpAppName } from "@libs/acp/utils/getAcpAppName";
import type { AxiosResponse } from "axios";
import { useNavigate, useLocation } from "react-router-dom";

import {
	CheckoutRedirectService,
	useRkCheckoutFlowExperiment,
} from "@libs/renegkit";

import { useFlowStore } from "@libs/acp/store/flow/useFlowStore";
import { useUserStore } from "@libs/acp/store/user/useUserStore";

import { handleFullFunnelRedirect } from "@libs/creditkit";
import {
	type AuthTypes,
	type CallBackNextStepsActionsProps,
	Flows,
} from "../types";

import type { CPNavigateState, FlowActionsHandlerProps } from "./types";
import { base64Encrypt } from "@libs/utils/helpers/base64";
import constants from "@libs/acp/common/constants";

const flowSlugNavigationPath = {
	[Flows.RESET_PASSWORD]: constants.pageNames.redefinePassword,
	[Flows.SIGNUP]: constants.pageNames.signup,
	[Flows.SIGNUP_UNIFIED]: constants.pageNames.signup,
	[Flows.SIGNUP_NCAD]: constants.pageNames.signupNCADCompleted,
	[Flows.SIGN_UP_PARTNER]: constants.pageNames.signUpPartnerCompleted,
	[Flows.SIMPLE_SIGNIN]: constants.pageNames.signin,
	[Flows.SIGNIN]: constants.pageNames.signin,
	[Flows.SIGNIN_OLD_CP]: constants.pageNames.signin,
	[Flows.SIGNIN_UNIFIED]: constants.pageNames.signin,
	[Flows.SIGNIN_CROSS]: constants.pageNames.signin,
	[Flows.SIGNIN_CROSS_COMPLEX_PASSWORD]: constants.pageNames.signin,
	[Flows.SIGNIN_CROSS_DISTINCT]: constants.pageNames.signin,
};

const useFlowActionsHandler = ({
	onSuccess,
	flowInfos,
}: FlowActionsHandlerProps) => {
	const { addAuthenticatedUser } = useUserStore();
	const {
		currentFlow: { activeFlows, currentStep, formType, stepsName },
		removeFlow,
		previousStep,
		goToStep,
		resetFlowStore,
		resetDocumentResponse,
		getFormState,
	} = useFlowStore();

	const navigate = useNavigate();
	const location = useLocation();
	const { search } = location;

	const searchParams = new URLSearchParams(search);

	const appName = getAcpAppName();

	const requiredUpdatesNavigationPath = {
		email: flowInfos[appName].unificationValidateEmail!,
		email_expired: flowInfos[appName].unificationUpdateEmail!,
		phone_expired: flowInfos[appName].unificationUpdatePhone!,
		password: flowInfos[appName].unificationUnifyPassword!,
	} as { [key: string]: string };

	//region redirects after authentication
	const handleRedirectAfterAuth = () => {
		const hasRedirectAfterAuthParam = searchParams.get("redirect_after_auth");

		if (hasRedirectAfterAuthParam) {
			navigate(hasRedirectAfterAuthParam, { replace: true });

			return true;
		}

		return false;
	};

	const handleFullFunnelDefaultRedirect = (): boolean => {
		const { isFullFunnelOrigin, fullFunnelCheckURL, partnerSlug } =
			handleFullFunnelRedirect();

		if (isFullFunnelOrigin) {
			setTimeout(() => {
				navigate(fullFunnelCheckURL, { state: { partnerSlug } });
			}, 0);

			return true;
		}

		return false;
	};

	const handleRequiredUpdatesRedirect = (
		required_updates: string[],
	): boolean => {
		const priorRequiredUpdate = required_updates?.[0];

		if (
			priorRequiredUpdate &&
			requiredUpdatesNavigationPath[priorRequiredUpdate]
		) {
			navigate(requiredUpdatesNavigationPath[priorRequiredUpdate]);

			resetFlowStore();
			return true;
		}

		return false;
	};

	const { isRenegkitVariant } = useRkCheckoutFlowExperiment();

	const handleSpecificAppRedirect = {
		ac: (source?: string) => {
			const checkoutRedirect = CheckoutRedirectService.getCheckoutRedirectUrl(
				undefined,
				isRenegkitVariant,
			);

			if (checkoutRedirect) {
				sessionStorage.setItem("temp_checkoutRedirect", checkoutRedirect);

				//TODO: wait next tick
				setTimeout(() => {
					navigate(checkoutRedirect, {
						replace: true,
						state: { source, isFromCheckoutRedirect: true },
					});
				}, 0);

				return true;
			}

			return false;
		},
		cp: () => {},
	};

	const handleDefaultAppRedirect = (navigateState?: CPNavigateState) => {
		setTimeout(() => {
			navigate(
				`${navigateState?.redirectURL || flowInfos[appName].homePath}${window.location.search}`,
				{
					replace: true,
					state: navigateState ? { ...navigateState } : {},
				},
			);
		}, 0);
	};

	const handleStoredRedirectAfterLogin = (): boolean => {
		const redirectPath = localStorage.getItem("redirectAfterLogin");
		if (redirectPath) {
			localStorage.removeItem("redirectAfterLogin");
			setTimeout(() => {
				navigate(redirectPath, { replace: true });
			}, 0);
			return true;
		}
		return false;
	};

	const handleRedirectOnFinishFlow = ({
		required_updates,
		navigateState,
		source,
	}: {
		required_updates?: string[];
		navigateState?: CPNavigateState;
		source?: string;
	}) => {
		const redirectAfterAuth = handleRedirectAfterAuth();
		if (redirectAfterAuth) return;

		if (required_updates && required_updates.length > 0) {
			const redirectToUnificationJustifyData =
				handleRequiredUpdatesRedirect(required_updates);

			if (redirectToUnificationJustifyData) return;
		}

		const redirected =
			handleSpecificAppRedirect[appName as "ac" | "cp"](source);
		if (redirected) return;

		const redirectToFullFunnel = handleFullFunnelDefaultRedirect();
		if (redirectToFullFunnel) return;

		const redirectedFromStorage = handleStoredRedirectAfterLogin();
		if (redirectedFromStorage) return;

		handleDefaultAppRedirect(navigateState);
	};

	//region handle finish
	const handleACPResponseToAPPs = ({
		response,
		authType,
		useProviderSuccess = false,
		source,
	}: {
		response: AxiosResponse;
		authType?: AuthTypes;
		useProviderSuccess?: boolean;
		source?: string;
	}) => {
		const { customer } = response.data;

		//#region AC
		if (appName === "ac") {
			if (useProviderSuccess) {
				onSuccess?.(customer);
			} else {
				onSuccess?.(response);
			}

			if (activeFlows.includes(Flows.SIGNUP_UNIFIED)) return;

			handleRedirectOnFinishFlow({
				required_updates: customer.required_updates,
				source,
			});
		}

		//#region CP mapper
		if (appName === "cp") {
			const redirect = searchParams.get("redirect");

			const redirectUrl = redirect || constants.pageNames.cp.home;

			const navigateState = {
				document: customer.document as string,
				authType: authType,
				isUnified: customer.isUnified,
				redirectURL: redirectUrl,
			};

			if (activeFlows.includes(Flows.SIGNUP_UNIFIED)) return;

			handleRedirectOnFinishFlow({
				required_updates: customer.required_updates,
				navigateState,
				source,
			});
		}
	};

	//region handle previous
	const previousStepActions = {
		ValidationMethodsStep: () => {
			if (activeFlows.includes(Flows.MFA_REQUIRED)) {
				removeFlow(Flows.MFA_REQUIRED);
			}
		},
		MFAEmailVerificationCodeStep: () => {
			removeFlow(Flows.MFA_EMAIL_REQUIRED);
		},
		PhoneStep: () => {
			if (activeFlows.includes(Flows.MFA_EMAIL_REQUIRED)) {
				if (formType === "sign-up") goToStep("EmailStep");

				removeFlow(Flows.MFA_EMAIL_REQUIRED);
			}
		},
		PasswordStep: () => {
			if (activeFlows.includes(Flows.MFA_REQUIRED)) {
				if (formType === "sign-up") goToStep("PhoneStep");

				removeFlow(Flows.MFA_REQUIRED);
			}

			const shouldResetActiveFlows = constants.signinWithPasswordOnly.some(
				(flow) => activeFlows.includes(flow),
			);
			if (shouldResetActiveFlows) {
				resetDocumentResponse();

				activeFlows.forEach((flow) => {
					if (constants.signinWithPasswordOnly.includes(flow)) {
						removeFlow(flow);
					}
				});
			}
		},
		MFAValidationMethodsStep: () => {
			removeFlow(Flows.MFA_REQUIRED);
		},
		KBAStep: () => {
			if (activeFlows.includes(Flows.EXTEND_FLOW)) {
				removeFlow(Flows.EXTEND_FLOW);
			}
		},
		MFAVerificationCodeStep: () => {
			if (formType === "sign-up" && activeFlows.includes(Flows.MFA_REQUIRED)) {
				removeFlow(Flows.MFA_REQUIRED);
			}
		},
	};

	const callBackPreviousStepsActions = (
		step: keyof typeof previousStepActions,
	) => {
		previousStepActions[step]?.();
	};

	const handlePreviousStep = () => {
		if (
			(activeFlows.includes(Flows.UPDATE_CONTACT) ||
				activeFlows.includes(Flows.UPDATE_PASSWORD)) &&
			stepsName.indexOf(currentStep) === 0
		) {
			resetFlowStore();
			navigate(-1);
			return;
		}

		previousStep();

		callBackPreviousStepsActions(
			currentStep as keyof typeof previousStepActions,
		);
	};

	//region handle next
	const callBackNextStepsActions = ({
		data,
		error,
		status,
		step,
		nextStepActions,
		errorNextStepActions,
	}: CallBackNextStepsActionsProps) => {
		if (status === "success") return nextStepActions[step]?.(data);

		return errorNextStepActions[step]?.(error);
	};

	const addHiddenData = (response: AxiosResponse) => {
		const { customer } = response.data;

		const authenticatedUserData = {
			...response.data,
			customer: {
				...customer,
				hidden_email: getFormState()["hidden_email"] || "",
				hidden_phone: getFormState()["hidden_phone"] || "",
				isUnified: customer.isUnified,
			},
		};

		return authenticatedUserData;
	};

	const finishFlow = ({
		response,
		shouldResetFlow = true,
		source,
	}: {
		response: AxiosResponse;
		shouldResetFlow?: boolean;
		source?: string;
	}) => {
		const authenticatedUserData = addHiddenData(response);

		addAuthenticatedUser(authenticatedUserData, "acp");

		handleACPResponseToAPPs({ response, source });

		if (shouldResetFlow) resetFlowStore();
	};

	const handleFlowSlugNavigations = (
		flowSlug: Flows,
		document: string,
		response?: AxiosResponse,
	) => {
		const hasDocumentInParams = searchParams.has("c");

		if (!hasDocumentInParams) {
			const encryptedDocument = base64Encrypt(document);
			searchParams.append("c", encryptedDocument);
		} else {
			const encryptedCurrentFlowDocument = base64Encrypt(document);
			const currentDocumentInParams = searchParams.get("c");

			if (encryptedCurrentFlowDocument !== currentDocumentInParams)
				searchParams.set("c", encryptedCurrentFlowDocument);
		}

		const navigateSearchParams = `?${searchParams.toString().replace("%3D", "=")}`;

		const flowSlugPath =
			flowSlugNavigationPath[flowSlug as keyof typeof flowSlugNavigationPath];

		const navigatePath = `${flowSlugPath}${navigateSearchParams}`;

		let navigateState = {
			document: document,
		} as {
			document: string;
			documentResponse?: {
				data: {
					flow_slug: Flows;
					flow_id: string;
					can_register: boolean;
				};
			};
		};

		if (response) {
			navigateState = {
				...navigateState,
				documentResponse: { data: response?.data },
			};
		}

		const shouldPassNavigateState = [
			Flows.RESET_PASSWORD,
			Flows.SIGNIN,
			Flows.SIGNIN_OLD_CP,
			Flows.SIGNIN_UNIFIED,
			Flows.SIGNIN_CROSS,
			Flows.SIMPLE_SIGNIN,
			Flows.SIGNIN_CROSS_DISTINCT,
		].includes(flowSlug);

		navigate(navigatePath, {
			replace: true,
			state: shouldPassNavigateState ? navigateState : undefined,
		});

		return;
	};

	return {
		finishFlow,
		handleACPResponseToAPPs,
		handlePreviousStep,
		callBackNextStepsActions,
		handleDefaultAppRedirect,
		handleSpecificAppRedirect,
		handleRedirectOnFinishFlow,
		handleFlowSlugNavigations,
		handleRedirectAfterAuth,
	};
};

export default useFlowActionsHandler;
